import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import useSWR from "swr";

/**
 * 매입/위탁 고객 검색 자동완성 — 입력을 디바운스해 /manager/change-request/consultation-users 조회.
 * 상담일지 등록요청의 "기존고객" 연동용 (매니저어드민 고객검색 팝업과 동일 검색 기준: 고객명/연락처/계좌).
 * - onSelect(item): 선택 시 호출 (item: {id, user_name, user_phone, bank_name, bank_number, bank_account, bank_info, email})
 * - 선택 후 표시는 부모(선택 고객 카드)가 담당하므로 입력값은 선택 시 비운다.
 */
function CustomerAutocomplete({ onSelect, placeholder }) {
  const [text, setText] = useState(""); // 입력 원문
  const [term, setTerm] = useState(""); // 디바운스된 검색어
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);

  // 전역 fetcher 는 success:false(권한 등)와 네트워크 오류에서 throw — error 를 표시하지 않으면
  // 오류가 "검색 결과 없음"으로 보여 신규로 오판하게 되므로 드롭다운에 실패 사유를 노출한다.
  const { data, error } = useSWR(
    term.trim().length >= 1
      ? `/manager/change-request/consultation-users?search=${encodeURIComponent(
          term.trim()
        )}`
      : null
  );
  const list = Array.isArray(data?.users) ? data.users : [];

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const onType = (e) => {
    const v = e.target.value;
    setText(v);
    setOpen(true);
    setActive(0);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setTerm(v), 400);
  };

  const choose = (item) => {
    onSelect(item);
    setText("");
    setTerm("");
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || list.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, list.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (list[active]) choose(list[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <Wrap ref={wrapRef}>
      <Field
        value={text}
        placeholder={placeholder || "고객명, 연락처, 계좌번호 검색"}
        onFocus={() => text && setOpen(true)}
        onChange={onType}
        onKeyDown={onKeyDown}
      />
      {open && term.trim() && (
        <List>
          {error ? (
            <ErrorMsg>
              {error?.serverPayload?.alert ||
                "검색에 실패했습니다. 잠시 후 다시 시도해주세요."}
            </ErrorMsg>
          ) : list.length === 0 ? (
            <Empty>검색 결과가 없습니다</Empty>
          ) : (
            list.map((it, i) => (
              <ItemBtn
                key={it.id}
                type="button"
                active={i === active}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(it);
                }}
              >
                <Nm>
                  {it.user_name} {it.user_phone}
                </Nm>
                {it.bank_info && it.bank_info.trim() && (
                  <Bank>{it.bank_info.trim()}</Bank>
                )}
              </ItemBtn>
            ))
          )}
        </List>
      )}
    </Wrap>
  );
}

const Wrap = styled.div`
  position: relative;
  width: 100%;
`;
const Field = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 14px;
  font-size: 15px;
  border: 1px solid #d9dde2;
  border-radius: 10px;
  background: #ffffff;
  color: #222222;
  outline: none;
  &:focus {
    border-color: #003580;
  }
  &::placeholder {
    color: #b8bcc2;
  }
`;
const List = styled.div`
  position: absolute;
  z-index: 30;
  top: 52px;
  left: 0;
  right: 0;
  max-height: 260px;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #e6e8eb;
  border-radius: 10px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  padding: 4px;
`;
const ItemBtn = styled.button`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  font-size: 14px;
  color: #222;
  background: ${({ active }) => (active ? "#eef3fb" : "transparent")};
  border: none;
  border-radius: 8px;
  cursor: pointer;
`;
const Nm = styled.span`
  word-break: break-word;
  font-weight: 500;
`;
const Bank = styled.span`
  font-size: 12px;
  color: #888;
`;
const Empty = styled.div`
  padding: 12px;
  font-size: 13px;
  color: #888;
  text-align: center;
`;
const ErrorMsg = styled.div`
  padding: 12px;
  font-size: 13px;
  color: #fb4d44;
  text-align: center;
`;

export default CustomerAutocomplete;
